import { useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Collapse from "@mui/material/Collapse";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import { AdminCatalogDoc, AdminManifest } from "@equipped/core/backend/client";
import { EditorTree as EditorTreeState } from "./useEditorTree";
import { SelectedNode, selectedNodeKey } from "./SelectedNode";
import { ownedCatalogIdFromUrl, ownedPackIdFromUrl } from "./entryUrls";
import { CatalogDraft, unassignedPackIds } from "./treeModel";

interface CatalogEntryLike {
  kind: "pack" | "catalog";
  id: string;
  title: string;
  manifestUrl?: string;
  url?: string;
}

function catalogKeyFor(id: string): string {
  return `catalog:${id}`;
}
function packKeyFor(id: string): string {
  return `manifest:${id}`;
}

/**
 * The Catalogs → Packs → Lessons tree (docs/343 v0 §2) — a projection over the
 * normalized document set `useEditorTree` holds, not a denormalized blob. Nested
 * `List`/`Collapse` (no `@mui/x-tree-view` dependency, matching `DrawerContent.tsx`'s
 * own established pattern, docs/337).
 */
export function EditorTree({
  tree,
  selected,
  onSelect,
  onCreateCatalog,
  onCreatePack,
}: {
  tree: EditorTreeState;
  selected: SelectedNode | null;
  onSelect: (node: SelectedNode) => void;
  onCreateCatalog: () => void;
  onCreatePack: () => void;
}) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggle = (key: string) =>
    setExpanded((cur) => {
      const next = new Set(cur);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const selectedKey = selected ? selectedNodeKey(selected) : null;

  // Every catalog KEY currently open (from the loaded index + any unsaved new
  // catalog drafts) — `Object.keys(tree.docs)` already covers both. v0: every
  // open catalog renders as a root row (no nested-only hiding) — a catalog that's
  // ALSO referenced as a nested entry elsewhere simply appears twice, which is
  // honest (it really is reachable both ways).
  const allCatalogKeys = Object.keys(tree.docs).filter((k) => tree.docs[k].kind === "catalog");
  const allCatalogs: CatalogDraft[] = allCatalogKeys.map((k) => tree.docs[k].draft as CatalogDraft);
  const resolveOwnedPackId = (url: string | undefined) => ownedPackIdFromUrl(tree.backendUrl, url);
  const unassigned = unassignedPackIds(tree.manifestIndex.map((m) => m.id), allCatalogs, resolveOwnedPackId);

  return (
    <Box sx={{ width: 340, borderRight: "1px solid", borderColor: "divider", overflowY: "auto" }}>
      <ListSubheader component="div" sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        Catalogs
        <Button size="small" onClick={onCreateCatalog}>
          + New
        </Button>
      </ListSubheader>
      <List dense disablePadding>
        {allCatalogKeys.map((key) => (
          <CatalogNode
            key={key}
            catalogKey={key}
            tree={tree}
            selectedKey={selectedKey}
            onSelect={onSelect}
            expanded={expanded}
            toggle={toggle}
            visited={new Set()}
            manifestIndex={tree.manifestIndex}
            catalogIndex={tree.catalogIndex}
          />
        ))}
      </List>

      <ListSubheader component="div" sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        Unassigned packs
        <Button size="small" onClick={onCreatePack}>
          + New
        </Button>
      </ListSubheader>
      <List dense disablePadding>
        {unassigned.map((id) => (
          <PackNode
            key={id}
            packId={id}
            tree={tree}
            selectedKey={selectedKey}
            onSelect={onSelect}
            expanded={expanded}
            toggle={toggle}
          />
        ))}
        {Object.keys(tree.docs)
          .filter((k) => tree.docs[k].kind === "manifest" && tree.docs[k].saved === null)
          .map((key) => (
            <PackNode
              key={key}
              packId={tree.docs[key].id}
              packKeyOverride={key}
              tree={tree}
              selectedKey={selectedKey}
              onSelect={onSelect}
              expanded={expanded}
              toggle={toggle}
            />
          ))}
      </List>
    </Box>
  );
}

function CatalogNode({
  catalogKey,
  tree,
  selectedKey,
  onSelect,
  expanded,
  toggle,
  visited,
  manifestIndex,
  catalogIndex,
}: {
  catalogKey: string;
  tree: EditorTreeState;
  selectedKey: string | null;
  onSelect: (n: SelectedNode) => void;
  expanded: Set<string>;
  toggle: (key: string) => void;
  visited: ReadonlySet<string>;
  manifestIndex: AdminManifest[];
  catalogIndex: AdminCatalogDoc[];
}) {
  const doc = tree.docs[catalogKey];
  if (!doc) return null;
  const isOpen = expanded.has(catalogKey);
  const node: SelectedNode = { type: "catalog", key: catalogKey };
  const nodeKey = `catalog:${catalogKey}`;
  const entries = (doc.draft.entries as CatalogEntryLike[] | undefined) ?? [];
  const nextVisited = doc.id ? new Set([...visited, doc.id]) : visited;

  return (
    <>
      <ListItemButton dense selected={selectedKey === nodeKey} onClick={() => onSelect(node)} sx={{ pl: 2 }}>
        {entries.length > 0 ? (
          <Box
            component="span"
            onClick={(e) => {
              e.stopPropagation();
              toggle(catalogKey);
            }}
            sx={{ width: 16, mr: 0.5, cursor: "pointer" }}
          >
            {isOpen ? "▾" : "▸"}
          </Box>
        ) : (
          <Box sx={{ width: 16, mr: 0.5 }} />
        )}
        {doc.loading ? (
          <CircularProgress size={14} sx={{ mr: 1 }} />
        ) : null}
        <ListItemText
          primary={doc.draft.title ? String(doc.draft.title) : "(untitled catalog)"}
          secondary={doc.saved === null ? "unsaved" : undefined}
        />
      </ListItemButton>
      <Collapse in={isOpen} timeout="auto" unmountOnExit={false}>
        <List dense disablePadding>
          {entries.map((entry, i) => {
            const entryNode: SelectedNode =
              entry.kind === "pack"
                ? { type: "packEntry", catalogKey, index: i }
                : { type: "catalogEntry", catalogKey, index: i };
            const entryKey = `${entryNode.type}:${catalogKey}:${i}`;
            const ownedPackId = entry.kind === "pack" ? ownedPackIdFromUrl(tree.backendUrl, entry.manifestUrl) : null;
            const ownedCatalogId = entry.kind === "catalog" ? ownedCatalogIdFromUrl(tree.backendUrl, entry.url) : null;
            return (
              <Box key={i}>
                <ListItemButton dense selected={selectedKey === entryKey} onClick={() => onSelect(entryNode)} sx={{ pl: 4 }}>
                  <ListItemText
                    primary={`${entry.kind === "pack" ? "📦" : "📁"} ${entry.title || "(untitled)"}`}
                  />
                </ListItemButton>
                {ownedPackId ? (
                  <PackNode
                    packId={ownedPackId}
                    tree={tree}
                    selectedKey={selectedKey}
                    onSelect={onSelect}
                    expanded={expanded}
                    toggle={toggle}
                    indent={6}
                  />
                ) : null}
                {ownedCatalogId && !nextVisited.has(ownedCatalogId) ? (
                  <CatalogNode
                    catalogKey={catalogKeyFor(ownedCatalogId)}
                    tree={tree}
                    selectedKey={selectedKey}
                    onSelect={onSelect}
                    expanded={expanded}
                    toggle={toggle}
                    visited={nextVisited}
                    manifestIndex={manifestIndex}
                    catalogIndex={catalogIndex}
                  />
                ) : null}
              </Box>
            );
          })}
        </List>
      </Collapse>
    </>
  );
}

function PackNode({
  packId,
  packKeyOverride,
  tree,
  selectedKey,
  onSelect,
  expanded,
  toggle,
  indent = 2,
}: {
  packId: string;
  packKeyOverride?: string;
  tree: EditorTreeState;
  selectedKey: string | null;
  onSelect: (n: SelectedNode) => void;
  expanded: Set<string>;
  toggle: (key: string) => void;
  indent?: number;
}) {
  const key = packKeyOverride ?? packKeyFor(packId);
  const doc = tree.docs[key];
  const isOpen = expanded.has(key);
  const node: SelectedNode = { type: "pack", key };
  const nodeKey = `pack:${key}`;
  const lessons = doc && Array.isArray(doc.draft.lessons) ? (doc.draft.lessons as Record<string, unknown>[]) : [];

  const select = () => {
    if (!doc) tree.loadPack(packId);
    onSelect(node);
  };

  return (
    <>
      <ListItemButton dense selected={selectedKey === nodeKey} onClick={select} sx={{ pl: indent }}>
        {doc && lessons.length > 0 ? (
          <Box
            component="span"
            onClick={(e) => {
              e.stopPropagation();
              toggle(key);
            }}
            sx={{ width: 16, mr: 0.5, cursor: "pointer" }}
          >
            {isOpen ? "▾" : "▸"}
          </Box>
        ) : (
          <Box sx={{ width: 16, mr: 0.5 }} />
        )}
        {doc?.loading ? <CircularProgress size={14} sx={{ mr: 1 }} /> : null}
        <ListItemText
          primary={`📦 ${doc?.draft.title ? String(doc.draft.title) : packId}`}
          secondary={doc?.saved === null ? "unsaved" : undefined}
        />
        {doc?.loadError ? <Chip label="error" color="error" size="small" /> : null}
      </ListItemButton>
      {doc ? (
        <Collapse in={isOpen} timeout="auto" unmountOnExit={false}>
          <List dense disablePadding>
            {lessons.map((lesson, i) => {
              const lessonNode: SelectedNode = { type: "lesson", packKey: key, index: i };
              const lessonKey = `lesson:${key}:${i}`;
              return (
                <ListItemButton
                  key={i}
                  dense
                  selected={selectedKey === lessonKey}
                  onClick={() => onSelect(lessonNode)}
                  sx={{ pl: indent + 4 }}
                >
                  <ListItemText primary={`📄 ${lesson.title ? String(lesson.title) : "(untitled lesson)"}`} />
                </ListItemButton>
              );
            })}
          </List>
        </Collapse>
      ) : null}
    </>
  );
}
